import { getVenueSourceCategory, isExplicitTestVenueRecord } from "./publicVenueSafety.js";

const cleanClassificationText = (value = "") => String(value || "").trim().toLowerCase();

const hasCollectionItems = (value) => Array.isArray(value) ? value.some(Boolean) : Boolean(value);

const hasPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0;
};

const facilityPattern = /\b(centre|center|court|courts|stadium|arena|sports hall|sport hall|leisure|recreation|recreational|gym|gymnasium|facility|complex|venue)\b/i;
const organisationPattern = /\b(associations?|federations?|governing body|peak body|national sports? organisation|state sporting association|administrative office)\b/i;
const officePattern = /\b(head office|administrative office|national office|state office)\b/i;
const socialGroupPattern = /\b(social (?:badminton )?group|wechat group|whatsapp group|facebook group|meetup group|players? group)\b/i;
const governingNamePattern = /^badminton (australia|victoria|new south wales|nsw|queensland|south australia|western australia|tasmania|northern territory|act|new zealand|england|scotland|wales|ireland|canada|usa|united states|france|germany|europe|asia|oceania)$/i;
const directBadmintonEvidencePattern = /(\bbadminton\b|\bshuttle(?:cock)?\b|\u7fbd\u6bdb\u7403|\u7fbd\u7403|\ubc30\ub4dc\ubbfc\ud134|\u30d0\u30c9\u30df\u30f3\u30c8\u30f3)/i;
const conflictingOvertureCategories = new Set([
  "baseball_field",
  "basketball_court",
  "football_field",
  "golf_course",
  "ice_skating_rink",
  "soccer_field",
  "squash_court",
  "swimming_pool",
  "tennis_court",
  "tennis_stadium",
  "volleyball_court"
]);

export const isPublicCourtHireCentre = (record = {}) => {
  const listingType = cleanClassificationText(record.listingType || record.listing_type || record.kind);
  const name = cleanClassificationText(record.name || record.title);
  const descriptor = [
    record.type,
    record.venueType,
    record.venue_type,
    record.category,
    record.description,
    record.badge,
    ...(Array.isArray(record.amenities) ? record.amenities : [])
  ].filter(Boolean).join(" ");
  const ownerManaged = record.ownerCreated === true
    || record.owner_created === true
    || Boolean(record.ownerUid || record.owner_uid)
    || ["claimed", "approved", "verified"].includes(cleanClassificationText(record.claimStatus || record.claim_status));
  const hasCourtInventory = hasPositiveNumber(record.courts || record.courtCount || record.court_count)
    || hasCollectionItems(record.courtPricing || record.court_pricing)
    || hasCollectionItems(record.courtsInventory || record.courts_inventory);
  const hasBookingPath = hasCollectionItems(record.bookingLinks || record.booking_links)
    || Boolean(record.bookingUrl || record.booking_url)
    || record.bookable === true;
  const hasNamedFacilityIdentity = facilityPattern.test(name);
  const hasFacilityIdentity = facilityPattern.test(`${name} ${descriptor}`);
  const sources = (Array.isArray(record.sources) ? record.sources : [record.source])
    .map(cleanClassificationText)
    .filter(Boolean);
  const sourceCategory = getVenueSourceCategory(record);
  const bookingEvidence = JSON.stringify(record.bookingLinks || record.booking_links || []);
  const hasDirectBadmintonEvidence = directBadmintonEvidencePattern.test([
    name,
    record.type,
    record.venueType,
    record.venue_type,
    sourceCategory,
    bookingEvidence
  ].filter(Boolean).join(" "));
  const conflictingOvertureRecord = sources.includes("overturemaps_places")
    && conflictingOvertureCategories.has(sourceCategory)
    && !hasDirectBadmintonEvidence;

  if (isExplicitTestVenueRecord(record)) return false;
  if (ownerManaged) return true;
  if (conflictingOvertureRecord) return false;
  if (socialGroupPattern.test(name)) return false;
  if (officePattern.test(name)) return false;
  if (["association", "federation", "group", "program"].includes(listingType)) return false;
  if ((governingNamePattern.test(name) || organisationPattern.test(`${name} ${descriptor}`)) && !hasCourtInventory && !hasNamedFacilityIdentity) return false;
  if (listingType === "club") return hasCourtInventory || (hasBookingPath && hasNamedFacilityIdentity);
  if (listingType === "venue") return true;
  return hasCourtInventory || hasBookingPath || hasFacilityIdentity || !listingType;
};

export const getPublicCentreClassification = (record = {}) => isPublicCourtHireCentre(record) ? "centre" : "community-organisation";
